import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { makeKey, getCache, setCache } from "@/lib/offlineCache";
import OfflineBadge from "@/components/OfflineBadge";
import DatePicker from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PlaceInput from "@/components/PlaceInput";
import { Moon, Sun, Calendar, Clock } from "lucide-react";

const DEFAULT_CITY = { name: "Delhi", country: "India", lat: 28.6139, lon: 77.2090, tz: "Asia/Kolkata" };

export default function Panchang() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [city, setCity] = useState(DEFAULT_CITY);
  const [panchang, setPanchang] = useState(null);
  const [hora, setHora] = useState(null);
  const [loading, setLoading] = useState(false);
  const [panchangCachedAt, setPanchangCachedAt] = useState(null);
  const [horaCachedAt, setHoraCachedAt] = useState(null);

  const fetchData = async (d, c) => {
    setLoading(true);
    const pKey = makeKey("panchang", { date: d, lat: c.lat, lon: c.lon, tz: c.tz });
    const hKey = makeKey("hora", { date: d, lat: c.lat, lon: c.lon, tz: c.tz });

    // Show cached versions instantly so the page stays useful offline
    const pCached = getCache(pKey);
    const hCached = getCache(hKey);
    if (pCached) { setPanchang(pCached.data); setPanchangCachedAt(pCached.cachedAt); }
    if (hCached) { setHora(hCached.data); setHoraCachedAt(hCached.cachedAt); }

    try {
      const [p, h] = await Promise.all([
        api.post("/astro/panchang", { date: d, lat: c.lat, lon: c.lon, tz: c.tz }),
        api.post("/astro/hora", { date: d, lat: c.lat, lon: c.lon, tz: c.tz }),
      ]);
      setPanchang(p.data); setPanchangCachedAt(null); setCache(pKey, p.data);
      setHora(h.data); setHoraCachedAt(null); setCache(hKey, h.data);
    } catch {
      // Offline or server error — keep cached copy (if any) on screen
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(date, city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6" data-testid="panchang-page">
      <header>
        <h1 className="font-serif text-4xl text-slate-50 mb-2">Daily Panchang & Hora</h1>
        <p className="text-slate-400">Tithi, Nakshatra, Yoga, Karana plus full 24-hora muhurat chart.</p>
      </header>

      <Card className="glass border-white/10 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-300">Date</Label>
            <div className="mt-1.5">
              <DatePicker
                value={date}
                onChange={setDate}
                testId="panchang-date-input"
                placeholder="Pick a date"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Location</Label>
            <div className="mt-1.5">
              <PlaceInput value={city} onChange={setCity} testId="panchang-city-select" />
            </div>
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => fetchData(date, city)}
              disabled={loading}
              data-testid="panchang-fetch-btn"
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium"
            >
              {loading ? "Loading..." : "Show Panchang"}
            </Button>
          </div>
        </div>
      </Card>

      {panchang && (
        <Card className="glass border-white/10 p-6 animate-fade-up" data-testid="panchang-detail">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-gold" />
              <h3 className="font-serif text-2xl text-slate-50">Panchang — {panchang.date}</h3>
            </div>
            {panchangCachedAt && <OfflineBadge cachedAt={panchangCachedAt} testId="panchang-offline-badge" />}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Detail label="Tithi" main={panchang.tithi.name} sub={`${panchang.tithi.paksha} • ${panchang.tithi.progress_percent}% complete`} />
            <Detail label="Nakshatra" main={panchang.nakshatra.name} sub={`Lord: ${panchang.nakshatra.lord}`} />
            <Detail label="Yoga" main={panchang.yoga} sub="" />
            <Detail label="Karana" main={panchang.karana} sub="" />
            <Detail label="Vaara" main={panchang.vaara} sub="Weekday" />
            <Detail label="Sun • Moon" main={`${panchang.sun_longitude}° • ${panchang.moon_longitude}°`} sub="Sidereal longitudes" />
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            {panchang.tithi.is_ekadashi && <Tag>Ekadashi (Vrat)</Tag>}
            {panchang.tithi.is_purnima && <Tag>Purnima (Full Moon)</Tag>}
            {panchang.tithi.is_amavasya && <Tag>Amavasya (New Moon)</Tag>}
            {panchang.tithi.is_chaturdashi && <Tag>Chaturdashi</Tag>}
          </div>
        </Card>
      )}

      {hora && (
        <Card className="glass border-white/10 p-6 animate-fade-up" data-testid="hora-detail">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Sun className="w-5 h-5 text-gold" />
              <h3 className="font-serif text-2xl text-slate-50">Hora Chart</h3>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {horaCachedAt && <OfflineBadge cachedAt={horaCachedAt} testId="hora-offline-badge" />}
              <div className="text-sm text-slate-400 flex items-center gap-4">
                <span><Calendar className="w-3.5 h-3.5 inline mr-1" />Sunrise {hora.sunrise}</span>
                <span><Clock className="w-3.5 h-3.5 inline mr-1" />Sunset {hora.sunset}</span>
              </div>
            </div>
          </div>

          {hora.current_hora && (
            <div className="mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="current-hora">
              <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">Current Hora</div>
              <div className="text-xl font-serif text-slate-50">{hora.current_hora.lord}</div>
              <div className="text-sm text-slate-300 mt-1">{hora.current_hora.start} – {hora.current_hora.end} • {hora.current_hora.period}</div>
              <div className="text-sm text-slate-400 mt-1">Favorable for: {hora.current_hora.favorable_for}</div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {hora.horas.map((h) => (
              <div
                key={h.index}
                className={`p-3 rounded-lg border ${h.lord === hora.current_hora?.lord && h.start === hora.current_hora?.start ? "bg-amber-500/15 border-amber-500/30" : "bg-slate-900/40 border-white/5"}`}
              >
                <div className="text-xs text-slate-500">{h.period} · #{h.index}</div>
                <div className="text-slate-100 font-medium">{h.lord}</div>
                <div className="text-xs text-slate-400">{h.start} – {h.end}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Detail({ label, main, sub }) {
  return (
    <div className="p-4 rounded-xl bg-slate-900/40 border border-white/5">
      <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">{label}</div>
      <div className="text-lg font-medium text-slate-50">{main}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="px-3 py-1 rounded-full text-sm bg-amber-500/15 text-amber-300 border border-amber-500/30 font-medium">
      {children}
    </span>
  );
}
